/**
 * Factor Actions Hook
 *
 * Handles factor submission and alternative selection.
 * All factor interactions go through the backend onboarding API.
 */

import { useCallback } from 'react';
import type { AaguidCatalog } from '@newcore/ui/passkey';
import {
  defaultOnboardingApiClient,
  type OnboardingApiClient,
} from '@/features/onboarding/api/onboarding-api-client';
import {
  SMS_RESEND_COUNTDOWN_SECONDS,
  FACTOR_ERROR_MESSAGES,
} from '@/features/onboarding/constants/onboarding.constants';
import {
  FactorType,
  FlowStatus,
  ChallengeStatus,
  type CurrentStepResponse,
  type OnboardingState,
  type OnboardingFlowResponse,
} from '@/features/onboarding/types/onboarding.types';
import { useAaguidCatalog } from '@/hooks/use-aaguid-catalog.queries';
import { webAuthnRejectionMessage } from '@/utils/webauthn';

const CODE_FACTOR_TYPES: ReadonlySet<FactorType> = new Set([
  FactorType.TOTP,
  FactorType.SMS_OTP,
  FactorType.WHATSAPP_OTP,
]);

interface UseFactorActionsProps {
  setState: React.Dispatch<React.SetStateAction<OnboardingState>>;
  processResponse: (response: OnboardingFlowResponse) => Promise<void>;
  /** Centralized /complete API call - handles guard against concurrent calls */
  callCompleteApi: () => Promise<string | null>;
  /** Injectable API client; defaults to the standard user-onboarding endpoints. */
  api?: OnboardingApiClient;
  /**
   * Override the auto-complete decision after a successful factor submission.
   * When omitted, defaults to firing /complete iff the response signals
   * canComplete=true, no currentStep, and no alternatives. Wizards with a
   * post-completion UX detour (e.g. the user-onboarding device-pairing
   * dialog) override this to defer /complete until the detour finishes —
   * /complete clears the onboarding session cookie, so any subsequent
   * session-authenticated call (like /onboarding/device/start-pair) must
   * happen before it.
   */
  shouldAutoComplete?: (response: OnboardingFlowResponse) => boolean;
}

/**
 * Default auto-complete decision: fire /complete after the last factor when
 * the backend signals the flow is ready to finish and the user has no
 * remaining alternatives to choose from.
 */
export const defaultShouldAutoComplete = (
  response: OnboardingFlowResponse
): boolean => {
  return (
    response.canComplete === true &&
    response.currentStep === null &&
    response.hasAlternative === false
  );
};

interface UseFactorActionsReturn {
  // Primary factor
  submitPrimaryFactor: (password: string) => Promise<boolean>;

  // Complete onboarding
  completeSecondaryFactors: () => Promise<boolean>;

  // Generic (for factory pattern)
  submitFactor: (payload: Record<string, unknown>) => Promise<boolean>;

  // Alternative selection
  chooseAlternative: (factorType: FactorType) => Promise<boolean>;
  goBackToAlternatives: () => Promise<void>;
}

interface FactorErrorMessageParams {
  factorType: FactorType;
  challengeStatus: ChallengeStatus;
  metadata: CurrentStepResponse['metadata'] | undefined;
  catalog: AaguidCatalog | undefined;
}

/**
 * Get user-friendly error message for a factor verification failure
 */
function getFactorErrorMessage({
  factorType,
  challengeStatus,
  metadata,
  catalog,
}: FactorErrorMessageParams): string {
  const messages = FACTOR_ERROR_MESSAGES[factorType];

  // A policy rejection names the reason; the canned lines below would
  // otherwise report "too many failed attempts" for a first attempt.
  const rejection = webAuthnRejectionMessage(
    metadata?.error_code,
    metadata?.aaguid,
    catalog
  );

  if (rejection) {
    return rejection;
  }

  if (!messages) {
    return 'Verification failed. Please try again.';
  }

  if (challengeStatus === ChallengeStatus.FAILED) {
    return messages.maxRetries;
  }

  if (challengeStatus === ChallengeStatus.RETRY_REQUIRED) {
    return messages.invalidCode;
  }

  return messages.generic;
}

/**
 * Hook for factor submission actions
 */
export function useFactorActions({
  setState,
  processResponse,
  callCompleteApi,
  api = defaultOnboardingApiClient,
  shouldAutoComplete = defaultShouldAutoComplete,
}: UseFactorActionsProps): UseFactorActionsReturn {
  const { data: catalog } = useAaguidCatalog();

  // ============================================================================
  // HELPERS
  // ============================================================================

  /**
   * Reset state when switching factors (choosing alternative or going back)
   * Clears error state and retry flags to ensure the new factor isn't locked out
   */
  const resetFactorSwitchState = useCallback(() => {
    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
      hasExceededRetries: false,
    }));
  }, [setState]);

  // ============================================================================
  // PRIMARY FACTOR (Password)
  // ============================================================================

  /**
   * Submit primary factor (password)
   */
  const submitPrimaryFactor = useCallback(
    async (password: string) => {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const response = await api.submitPassword(password);
        await processResponse(response);

        return true;
      } catch (error) {
        setState(prev => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Setup failed',
          isLoading: false,
        }));

        return false;
      }
    },
    [api, setState, processResponse]
  );

  // ============================================================================
  // COMPLETE ONBOARDING
  // ============================================================================

  /**
   * Complete secondary factors step — always calls the /complete API and
   * transitions the flow to COMPLETED. Any flow-specific detour (e.g. the
   * onboarding device-pairing prompt) is the wizard's responsibility: it can
   * gate its own "COMPLETED" render branch on whatever local state it needs.
   */
  const completeSecondaryFactors = useCallback(async (): Promise<boolean> => {
    const token = await callCompleteApi();

    return token !== null;
  }, [callCompleteApi]);

  // ============================================================================
  // GENERIC FACTOR SUBMISSION (for factory pattern)
  // ============================================================================

  /**
   * Generic factor submission - works for ANY factor type
   * Used by the FactorStepRenderer factory pattern
   * Backend determines what to do based on session state
   *
   * Handles RETRY_REQUIRED status by showing factor-specific error messages
   * without transitioning the flow state (user can retry the same step)
   *
   * Auto-completes when canComplete=true and no more factors to show,
   * instead of relying on useEffect which can miss due to timing issues.
   */
  const submitFactor = useCallback(
    async (payload: Record<string, unknown>) => {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const response = await api.submitFactor(payload);

        // Handle retry-required: verification failed but user can try again
        // Backend returns IN_PROGRESS with currentStep.status = RETRY_REQUIRED
        if (
          response.status === FlowStatus.IN_PROGRESS &&
          response.currentStep?.status === ChallengeStatus.RETRY_REQUIRED
        ) {
          const factorType = response.currentStep.factor_type;
          const errorMessage = getFactorErrorMessage({
            factorType,
            challengeStatus: ChallengeStatus.RETRY_REQUIRED,
            metadata: response.currentStep.metadata,
            catalog,
          });

          setState(prev => ({
            ...prev,
            error: errorMessage,
            hasExceededRetries: false,
            isLoading: false,
          }));

          return false;
        }

        // Handle flow failed (e.g., max retries exceeded)
        if (
          response.status === FlowStatus.FAILED &&
          response.currentStep?.factor_type
        ) {
          const factorType = response.currentStep.factor_type;
          const errorMessage = getFactorErrorMessage({
            factorType,
            challengeStatus: ChallengeStatus.FAILED,
            metadata: response.currentStep.metadata,
            catalog,
          });

          // The backend ends the flow, so FailedStep replaces the dead-end code input
          const isCodeFailure = CODE_FACTOR_TYPES.has(factorType);

          setState(prev => ({
            ...prev,
            ...(isCodeFailure && { flowStatus: FlowStatus.FAILED }),
            error: errorMessage,
            hasExceededRetries: true,
            isLoading: false,
          }));

          return false;
        }

        // Success or other status - process normally
        await processResponse(response);

        // Auto-complete: if canComplete=true and no more factors to show,
        // route through callCompleteApi so the backend also tears down the
        // flow (marks the invitation COMPLETED, clears the session cookie,
        // and — for onboarding — returns the exchange token). The previous
        // approach of setting flowStatus directly left the BFF/enrollment
        // state stuck in ACCESSED for invitation flows whose success screen
        // never surfaces a "continue" button.
        if (shouldAutoComplete(response)) {
          await callCompleteApi();
        }

        return true;
      } catch (error) {
        setState(prev => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Submission failed',
          isLoading: false,
        }));

        return false;
      }
    },
    [
      api,
      setState,
      processResponse,
      callCompleteApi,
      shouldAutoComplete,
      catalog,
    ]
  );

  // ============================================================================
  // ALTERNATIVE SELECTION
  // ============================================================================

  /**
   * Choose an alternative authenticator when hasAlternative=true
   * Backend initiates the selected factor's challenge
   */
  const chooseAlternative = useCallback(
    async (factorType: FactorType) => {
      resetFactorSwitchState();

      try {
        const response = await api.chooseAuthenticator(factorType);
        await processResponse(response);

        // Reset OTP state when selecting an OTP-based factor
        // Backend sends a new OTP code, so we need fresh countdown and resend count
        const isOtpFactor =
          factorType === FactorType.SMS_OTP ||
          factorType === FactorType.WHATSAPP_OTP;

        if (isOtpFactor) {
          setState(prev => ({
            ...prev,
            smsResendCountdown: SMS_RESEND_COUNTDOWN_SECONDS,
            smsResendCount: 0,
          }));
        }

        return true;
      } catch (error) {
        setState(prev => ({
          ...prev,
          error:
            error instanceof Error
              ? error.message
              : 'Failed to choose authenticator',
          isLoading: false,
        }));

        return false;
      }
    },
    [api, setState, processResponse, resetFactorSwitchState]
  );

  /**
   * Go back to alternatives selection
   * Calls the backend /authenticator-alternatives API to fetch fresh options
   */
  const goBackToAlternatives = useCallback(async () => {
    resetFactorSwitchState();

    try {
      const altResponse = await api.getAuthenticatorAlternatives();
      const alternatives = altResponse.alternatives.map(alt => ({
        factorType: alt.factorType as FactorType, // Backend already returns factorType
        isEnrolled: alt.isEnrolled,
        displayName: alt.displayName,
        description: alt.description,
      }));

      setState(prev => ({
        ...prev,
        currentFactorType: null,
        currentStepMetadata: null,
        alternatives,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to fetch alternatives',
        isLoading: false,
      }));
    }
  }, [api, setState, resetFactorSwitchState]);

  return {
    submitPrimaryFactor,
    completeSecondaryFactors,
    submitFactor,
    chooseAlternative,
    goBackToAlternatives,
  };
}
